/**
 * Level 100's boss, Super Drunk (spec 02 §9.4): 3x3 sprites on the C64 (72x63 px), so about 9 tiles
 * across and 8 tall here (it stands 7.7 tall and draws about 8 wide at its turn) - Drunk grown huge,
 * with the detail that size affords: a round green robe with folds at the hem, a rope belt and a
 * pale cuff, the hood flopping back to a bobble, a big face with an angry brow, a red drinker's nose
 * and a frown, big hands out of wide sleeves, a green bottle. The ending's big figure walking back
 * and forth.
 *
 * A puppet (05 §6.1), feet at the origin, facing +x, turned 0.8 rad toward the camera; the face and
 * belt knot are turned a further 0.4 rad toward +z (the side the mirror always shows) so both eyes
 * read. Parts and pivots, in the figure's frame (a child's pivot is relative to its parent's):
 *   footNear (1.2, 0.1, 1.55), footFar (1.2, 0.1, -1.55)       ankles; the toes peep out under the hem
 *   body     (0, 0.3, 0)                                         the robe, hips just above the hem
 *   head     (0.3, 3.95, 0) on body                              the neck (world y 4.25)
 *   armNear  (0.3, 3.05, 2.6), armFar (0.3, 3.05, -2.6) on body  the shoulders (world y 3.35)
 *   bottle   about (1.92, -1.66, 1.39) on armNear               in the near hand (world about (2.2, 1.7, 4.0))
 * The robe, hood and sleeves are on the `bossRobe` ramp; `angry` draws them from the shared `angry`
 * ramp instead (face, hands, feet and bottle keep their own ramps, as Drunk's do).
 */

import { type Color, type Material, Vector3 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { bottle } from './projectiles';
import { Puppet } from './puppet';
import { lathe, rotX, Smooth, tube } from './powerups/shapes';

/** The robe's rest shape: (radius, height) from the hem round the belly to the collar. */
const ROBE: Array<[number, number]> = [
  [0, 0.1], [2.4, 0.1], [2.74, 0.26], [3.02, 0.7], [3.22, 1.3], [3.28, 1.85], [3.16, 2.4],
  [2.88, 2.95], [2.48, 3.42], [1.98, 3.8], [1.42, 4.08], [0.8, 4.24], [0, 4.28],
];

/**
 * How far the face and the belt knot are turned toward the camera side (+z). Facing left is the
 * mirror image, so +z is always the side the camera sees; turning them keeps both eyes in view.
 */
const TO_CAMERA = -0.4;

/** Folds round the hem, fading out by the belt. */
const folds = (a: number, y: number): number => 1 + 0.06 * Math.max(0, 1 - y / 2.2) * Math.cos(9 * a);

/** The robe's radius at height y (between profile points), before folds. */
function robeRadius(y: number): number {
  for (let i = 1; i < ROBE.length; i++) {
    const [r0, y0] = ROBE[i - 1] ?? [0, 0];
    const [r1, y1] = ROBE[i] ?? [0, 0];
    if (y >= y0 && y <= y1 && y1 > y0) return r0 + ((r1 - r0) * (y - y0)) / (y1 - y0);
  }
  return 0;
}

/** A ring round the robe at height y, following its folds, pushed out by `out`. */
function band(y: number, out: number, steps = 48): Vector3[] {
  const pts: Vector3[] = [];
  for (let j = 0; j < steps; j++) {
    const a = (j / steps) * Math.PI * 2;
    const r = robeRadius(y) * folds(a, y) + out;
    pts.push(new Vector3(Math.sin(a) * r, y, Math.cos(a) * r));
  }
  return pts;
}

function robe(p: Palette, ramp: string): MeshBuilder {
  const mb = new MeshBuilder();
  const s = new Smooth();
  lathe(s, ROBE, 44, (seg) => p.color(seg === 0 ? `${ramp}.0` : `${ramp}.2`), folds);
  tube(s, band(0.32, 0.02), 0.2, 10, p.color(`${ramp}.3`), { closed: true });
  // A rope belt, knotted at the front with its ends hanging.
  const rope = p.color('bossRope.1');
  tube(s, band(2.0, 0.02), 0.17, 10, rope, { closed: true });
  s.flush(mb);
  mb.with(place(0, 0, 0, TO_CAMERA), () => {
    for (const dz of [0.28, -0.28]) {
      const end: Vector3[] = [];
      for (let i = 0; i <= 5; i++) {
        const y = 1.95 - i * 0.2;
        end.push(new Vector3(robeRadius(y) + 0.12 - i * 0.01, y, dz * (1 + i * 0.12)));
      }
      tube(s, end, 0.13, 8, rope);
    }
    s.flush(mb);
    mb.blob(0.3, 0.26, 0.34, p.color('bossRope.2'), place(robeRadius(2.0) + 0.1, 2.0, 0), { detail: 2 });
    for (const dz of [0.33, -0.33]) {
      mb.blob(0.17, 0.2, 0.17, p.color('bossRope.2'), place(robeRadius(1.0) + 0.14, 0.94, dz * 1.6), { detail: 2 });
    }
  });
  return mb;
}

/** Where the face ellipsoid's front surface is, for things set on it. */
const FACE = { x: 1.4, y: 1.3, rx: 1.0, ry: 1.4, rz: 1.45 };
const faceX = (y: number, z: number): number =>
  FACE.x + FACE.rx * Math.sqrt(Math.max(0, 1 - ((y - FACE.y) / FACE.ry) ** 2 - (z / FACE.rz) ** 2));

function face(mb: MeshBuilder, p: Palette, ramp: string): void {
  const s = new Smooth();
  // The hood's pale rim round the face.
  const rim: Vector3[] = [];
  for (let i = 0; i < 32; i++) {
    const a = (i / 32) * Math.PI * 2;
    rim.push(new Vector3(1.72, FACE.y + Math.sin(a) * 1.34, Math.cos(a) * 1.4));
  }
  tube(s, rim, 0.22, 10, p.color(`${ramp}.3`), { closed: true });
  // A frown, the corners pulled down.
  const frown: Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const z = -0.5 + (i / 8) * 1.0;
    const y = 0.42 + 0.2 * (1 - (z / 0.5) ** 2);
    frown.push(new Vector3(faceX(y, z) - 0.02, y, z));
  }
  tube(s, frown, 0.1, 8, p.color('mouth.0'));
  s.flush(mb);
  mb.blob(FACE.rx, FACE.ry, FACE.rz, p.color('bossFace.1'), place(FACE.x, FACE.y, 0), { detail: 3 });
  for (const side of [1, -1]) {
    const z = side * 0.42;
    mb.blob(0.26, 0.52, 0.34, p.color('eye.1'), place(faceX(1.66, z) - 0.14, 1.66, z), { detail: 3 });
    mb.blob(0.12, 0.26, 0.15, p.color('eye.0'), place(faceX(1.54, z * 0.85) + 0.02, 1.54, z * 0.85), { detail: 2 });
    // The brow, heavy and low over the nose: angry.
    mb.blob(0.24, 0.17, 0.56, p.color('bossBrow.1'), rotX(faceX(2.14, z) - 0.02, 2.14, z * 1.08, -side * 0.5), {
      detail: 2,
    });
    mb.blob(0.1, 0.22, 0.28, p.color('bossNose.2'), place(faceX(0.95, side * 0.9) - 0.06, 0.95, side * 0.9), {
      detail: 2,
    });
  }
  mb.blob(0.42, 0.38, 0.44, p.color('bossNose.1'), place(faceX(1.12, 0) + 0.02, 1.12, 0), { detail: 3 });
  mb.blob(0.1, 0.08, 0.12, p.color('bossNose.2'), place(faceX(1.12, 0) + 0.26, 1.26, 0.12), { detail: 1 });
}

function head(p: Palette, ramp: string): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(2.1, 1.8, 2.05, p.color(`${ramp}.2`), place(0, 1.45, 0), { detail: 3 });
  const s = new Smooth();
  // The hood's tip, flopping back and down to a bobble.
  const tip = [
    new Vector3(-0.9, 2.55, 0), new Vector3(-1.6, 2.95, 0), new Vector3(-2.3, 2.95, 0),
    new Vector3(-2.85, 2.6, 0), new Vector3(-3.15, 2.0, 0),
  ];
  tube(s, tip, [1.05, 0.72, 0.5, 0.34, 0.22], 14, p.color(`${ramp}.2`));
  s.flush(mb);
  mb.blob(0.4, 0.4, 0.4, p.color(`${ramp}.3`), place(-3.2, 1.75, 0), { detail: 3 });
  mb.with(place(0, 0, 0, TO_CAMERA), () => face(mb, p, ramp));
  return mb;
}

/** The sleeve's path from the shoulder, forward, down and out over the robe (`side` 1 near, -1 far). */
const sleeve = (side: 1 | -1): Vector3[] => [
  new Vector3(-0.4, 0.0, -0.4 * side), new Vector3(0.3, -0.5, 0.45 * side), new Vector3(0.9, -1.05, 0.75 * side),
  new Vector3(1.4, -1.4, 0.85 * side),
];
const cuffDir = (side: 1 | -1): Vector3 => new Vector3(0.5, -0.35, 0.1 * side).normalize();
/** Where the hand is, in the arm's frame. */
const handAt = (side: 1 | -1): Vector3 =>
  (sleeve(side)[3] ?? new Vector3()).clone().addScaledVector(cuffDir(side), 0.55);

/**
 * A sleeve reaching forward and down from the shoulder, a pale cuff, a big hand; `side` 1 for the
 * near arm (out toward +z, gripping the bottle), -1 for the far one (a fist).
 */
function arm(p: Palette, ramp: string, side: 1 | -1): MeshBuilder {
  const mb = new MeshBuilder();
  const s = new Smooth();
  const path = sleeve(side);
  // A shade darker than the robe: a figure has only its outer outline, so the arm needs its own tone.
  tube(s, path, [0.6, 0.8, 0.8, 0.9], 14, p.color(`${ramp}.1`));
  const dir = cuffDir(side);
  const end = path[3] ?? new Vector3();
  const cuff = [end.clone().addScaledVector(dir, -0.18), end.clone().addScaledVector(dir, 0.1)];
  tube(s, cuff, 0.95, 14, p.color(`${ramp}.3`));
  s.flush(mb);
  const h = handAt(side);
  const skin = p.color('bossFace.1');
  mb.blob(0.55, 0.5, 0.5, skin, place(h.x, h.y, h.z), { detail: 3 });
  if (side === 1) {
    // Gripping the bottle (the `bottle` part, just in front): fingers round its side, the thumb over its front.
    for (const dy of [-0.26, 0, 0.26]) {
      mb.blob(0.15, 0.13, 0.17, skin, place(h.x + 0.46, h.y + dy, h.z + 0.6), { detail: 1 });
    }
    mb.blob(0.16, 0.28, 0.14, skin, place(h.x + 0.06, h.y + 0.12, h.z + 0.84, 0, 1, 0, -0.4), { detail: 2 });
  } else {
    const knuckle: Color = p.color('bossFace.0');
    mb.blob(0.2, 0.32, 0.2, skin, place(h.x + 0.2, h.y + 0.42, h.z - 0.3, 0, 1, 0, -0.5), { detail: 2 });
    for (const dy of [-0.2, 0, 0.2]) {
      mb.blob(0.12, 0.1, 0.1, knuckle, place(h.x + 0.45, h.y + dy, h.z - 0.28), { detail: 1 });
    }
  }
  return mb;
}

function foot(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(1.05, 0.62, 0.7, p.color('bossFeet.1'), place(0.35, 0, 0), {
    topColor: p.color('bossFeet.2'), flatBottom: true, detail: 3,
  });
  return mb;
}

/** Super Drunk, as a puppet the renderer can walk and bob (see the part list above). */
export function buildBoss(p: Palette, material: Material, angry = false): Puppet {
  const ramp = angry ? 'angry' : 'bossRobe';
  const pup = new Puppet(material);
  pup.turn = 0.8;
  pup.add('footNear', foot(p), 1.2, 0.1, 1.55);
  pup.add('footFar', foot(p), 1.2, 0.1, -1.55);
  pup.add('body', robe(p, ramp), 0, 0.3, 0);
  pup.add('head', head(p, ramp), 0.3, 3.95, 0, 'body');
  pup.add('armNear', arm(p, ramp, 1), 0.3, 3.05, 2.6, 'body');
  pup.add('armFar', arm(p, ramp, -1), 0.3, 3.05, -2.6, 'body');
  const b = new MeshBuilder();
  bottle(b, p, place(0, 0.2, 0, 0, 2.6, 0, -0.25));
  const h = handAt(1);
  pup.add('bottle', b, h.x + 0.08, h.y + 0.05, h.z + 0.45, 'armNear');
  return pup;
}

